import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";

import {View} from "@khanacademy/wonder-blocks-core";
import {LabeledTextField} from "@khanacademy/wonder-blocks-form";

interface Props {
    /**
     * The given name of the user.
     */
    firstName: string | null | undefined;
    /**
     * The family name of the user.
     */
    lastName: string | null | undefined;
    /**
     * Callback to set the first name.
     */
    setFirstName: (firstName: string) => void;
    /**
     * Callback to set the last name.
     */
    setLastName: (lastName: string) => void;
    /**
     * Callback after validating the first name.
     */
    onValidateFirstName?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * Callback after validating the last name.
     */
    onValidateLastName?: (errorMessage?: string | null | undefined) => unknown;
    /**
     * Whether the fields are required.
     */
    required?: boolean;
}

/**
 * A set of fields for the user to enter their first and last name. This is a
 * component because we always ask for both names together.
 */
export function FullNameField({
    firstName,
    lastName,
    setFirstName,
    setLastName,
    onValidateFirstName,
    onValidateLastName,
    required,
}: Props): React.ReactNode {
    return (
        <View style={styles.nameFields}>
            <View style={styles.nameFieldWrapper}>
                <LabeledTextField
                    testId="signup-first-name"
                    label={t`First name`}
                    value={firstName || ""}
                    onChange={setFirstName}
                    validate={(value) => {
                        if (required && firstName && !value.length) {
                            return t`Please tell us your first name.`;
                        }
                    }}
                    onValidate={onValidateFirstName}
                    required={required}
                />
            </View>
            <View style={styles.nameFieldWrapper}>
                <LabeledTextField
                    testId="signup-last-name"
                    label={t`Last name`}
                    value={lastName || ""}
                    onChange={setLastName}
                    validate={(value) => {
                        if (required && lastName && !value.length) {
                            return t`Please tell us your last name.`;
                        }
                    }}
                    onValidate={onValidateLastName}
                    required={required}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    nameFields: {
        flexDirection: "row",
        alignItems: "top",
    },
    nameFieldWrapper: {
        width: "50%",
        ":first-of-type": {
            marginRight: 4,
        },
        ":last-of-type": {
            marginLeft: 4,
        },
    },
});
